import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Input, Label } from '@crestrewards/ui';
import { AlertCircle, Check, X } from 'lucide-react';

import AuthShell from './AuthShell';
import { checkPassword, validatePassword } from './authValidation';
import * as authApi from './authApi';

/**
 * Step two of a password reset: choose the new password.
 *
 * The token comes from the emailed link's query string. It is never displayed
 * and never put in a field the user could edit — it is a credential, and one
 * that would be trivially shoulder-surfed off a screen.
 */
export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') ?? '';

  const [values, setValues] = useState({ password: '', confirm: '' });
  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState('');
  const [pending, setPending] = useState(false);

  const rules = checkPassword(values.password);

  // A link with no token at all never had one — don't make the user fill in a
  // form that cannot possibly succeed.
  if (!token) {
    return (
      <AuthShell title="That link is incomplete" subtitle="It is missing its reset token.">
        <div className="space-y-4">
          <div
            role="alert"
            className="flex items-start gap-2 rounded-md border-hairline border-destructive/30 bg-destructive-tint px-3 py-2.5 text-meta text-destructive-on-tint"
          >
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <span>
              Open the link straight from your email — some clients wrap or truncate long
              links. If it still fails, request a new one.
            </span>
          </div>
          <Button asChild className="w-full">
            <Link to="/forgot-password">Request a new link</Link>
          </Button>
        </div>
      </AuthShell>
    );
  }

  function handleChange(field) {
    return (event) => {
      const { value } = event.target;
      setValues((current) => ({ ...current, [field]: value }));
      setFieldErrors((current) => {
        if (!current[field]) return current;
        const next = { ...current };
        delete next[field];
        return next;
      });
      setFormError('');
    };
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (pending) return;

    const errors = {};
    const passwordError = validatePassword(values.password);
    if (passwordError) errors.password = passwordError;
    if (values.password !== values.confirm) errors.confirm = 'Both passwords must match.';

    setFieldErrors(errors);
    setFormError('');
    if (Object.keys(errors).length > 0) return;

    setPending(true);
    try {
      await authApi.resetPassword({ token, password: values.password });
      // Straight to sign-in. Every session was revoked server-side, so there is
      // nothing to return to — and asking them to sign in proves the new
      // password works while they still remember it.
      navigate('/login', {
        replace: true,
        state: { notice: 'Password updated. Sign in with your new password.' },
      });
    } catch (error) {
      // 422s land on the field; an invalid or expired token is form-level.
      const details = error.details;
      if (details && typeof details === 'object' && details.password) {
        setFieldErrors({ password: [].concat(details.password)[0] });
      } else {
        setFormError(error.message);
      }
    } finally {
      setPending(false);
    }
  }

  return (
    <AuthShell title="Choose a new password" subtitle="This link works once.">
      <form onSubmit={handleSubmit} noValidate className="space-y-4">
        {formError ? (
          <div
            role="alert"
            className="flex items-start gap-2 rounded-md border-hairline border-destructive/30 bg-destructive-tint px-3 py-2.5 text-meta text-destructive-on-tint"
          >
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <div className="space-y-2">
              <span>{formError}</span>
              <Link
                to="/forgot-password"
                className="block underline underline-offset-4 hover:no-underline"
              >
                Request a new link
              </Link>
            </div>
          </div>
        ) : null}

        <div className="space-y-1.5">
          <Label htmlFor="password">New password</Label>
          <Input
            id="password"
            name="password"
            type="password"
            autoComplete="new-password"
            autoFocus
            value={values.password}
            onChange={handleChange('password')}
            disabled={pending}
            aria-invalid={Boolean(fieldErrors.password)}
            aria-describedby="password-rules"
          />
          <ul id="password-rules" className="space-y-1 pt-1">
            {rules.map((rule) => (
              <li
                key={rule.key}
                className={`flex items-center gap-1.5 text-label ${
                  rule.met ? 'text-success-on-tint' : 'text-muted-foreground'
                }`}
              >
                {rule.met ? (
                  <Check className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                ) : (
                  <X className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                )}
                {rule.label}
              </li>
            ))}
          </ul>
          {fieldErrors.password ? (
            <p className="text-label text-destructive-on-tint">{fieldErrors.password}</p>
          ) : null}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="confirm">Confirm new password</Label>
          <Input
            id="confirm"
            name="confirm"
            type="password"
            autoComplete="new-password"
            value={values.confirm}
            onChange={handleChange('confirm')}
            disabled={pending}
            aria-invalid={Boolean(fieldErrors.confirm)}
            aria-describedby={fieldErrors.confirm ? 'confirm-error' : undefined}
          />
          {fieldErrors.confirm ? (
            <p id="confirm-error" className="text-label text-destructive-on-tint">
              {fieldErrors.confirm}
            </p>
          ) : null}
        </div>

        <Button type="submit" className="w-full" disabled={pending}>
          {pending ? 'Updating…' : 'Set new password'}
        </Button>
      </form>
    </AuthShell>
  );
}
